import { axiosClient } from '@/libs/axios-client';

/**
 * Retrieves the timeline (snapshots + audit logs) for a specific document.
 *
 * @param {string} modelName - The model name (e.g., "User", "Employee").
 * @param {string} documentId - The document ID.
 * @returns {Promise<Object>} A promise that resolves with the timeline data.
 * @throws Will throw an error if the request fails.
 */
export const getTimeline = async (modelName, documentId) => {
  try {
    const response = await axiosClient.get(
      `/history/${modelName}/${documentId}/timeline`
    );
    return response.data;
  } catch (error) {
    console.error(
      `Failed to retrieve timeline for ${modelName}/${documentId}:`,
      error
    );
    throw error;
  }
};

/**
 * Retrieves all snapshots for a specific document.
 *
 * @param {string} modelName - The model name (e.g., "User", "Employee").
 * @param {string} documentId - The document ID.
 * @returns {Promise<Object>} A promise that resolves with the snapshots data.
 * @throws Will throw an error if the request fails.
 */
export const getSnapshots = async (modelName, documentId) => {
  try {
    const response = await axiosClient.get(
      `/history/${modelName}/${documentId}/snapshots`
    );
    return response.data;
  } catch (error) {
    console.error(
      `Failed to retrieve snapshots for ${modelName}/${documentId}:`,
      error
    );
    throw error;
  }
};

/**
 * Retrieves all audit logs for a specific document.
 *
 * @param {string} modelName - The model name (e.g., "User", "Employee").
 * @param {string} documentId - The document ID.
 * @returns {Promise<Object>} A promise that resolves with the audit logs data.
 * @throws Will throw an error if the request fails.
 */
export const getAuditLogs = async (modelName, documentId) => {
  try {
    const response = await axiosClient.get(
      `/history/${modelName}/${documentId}/audit-logs`
    );
    return response.data;
  } catch (error) {
    console.error(
      `Failed to retrieve audit logs for ${modelName}/${documentId}:`,
      error
    );
    throw error;
  }
};

/**
 * Retrieves statistics about document history.
 *
 * @param {string} modelName - The model name (e.g., "User", "Employee").
 * @param {string} documentId - The document ID.
 * @returns {Promise<Object>} A promise that resolves with the statistics data.
 * @throws Will throw an error if the request fails.
 */
export const getStatistics = async (modelName, documentId) => {
  try {
    const response = await axiosClient.get(
      `/history/${modelName}/${documentId}/statistics`
    );
    return response.data;
  } catch (error) {
    console.error(
      `Failed to retrieve statistics for ${modelName}/${documentId}:`,
      error
    );
    throw error;
  }
};

const HistoryService = {
  getTimeline,
  getSnapshots,
  getAuditLogs,
  getStatistics,
};

export default HistoryService;
